<template>
  <div>
    <NavigationBreadcrumb
      :items="breadcrumb_items"
      :matterId="egress_profile_matter_id"
      :baseMatterId="matter_base_id"
      :showEgressProfile="true"
      :showMatrix1="true"
      :showMatrix2="true"
      :showMatrix3="true"
      :showMatrix4="true"
      :showStudyPlan="true"
      :showMatterProgram="true"
      :showCurricularMesh="true"
      :showPlannification="true"
    ></NavigationBreadcrumb>

    <div>
      <h4
        class="mt-3 d-flex align-items-center flex-wrap justify-content-center"
      >
        <span>{{ $t("teaching.instructional.title") }}</span>
        <AlternativeNameButton
          :table_name="'manual.matter_program'"
          :allows_crud="allows_crud"
        ></AlternativeNameButton>
      </h4>
    </div>

    <div class="d-flex align-items-center justify-content-center mb-2">
      <span class="kl-method-label mr-2">
        {{ $t("teaching.instructional.method") }}:
      </span>
      <b-form-select
        v-if="allows_crud && instructionalDesign"
        v-model="selected_method_id"
        :options="methodOptions"
        :disabled="saving_method"
        size="sm"
        class="kl-method-select"
        v-b-tooltip.v-secondary.noninteractive="
          $t('teaching.instructional.methodTooltip')
        "
        @change="changeMethod"
      ></b-form-select>
      <span v-else class="kl-method">
        {{
          activeMethod
            ? activeMethod.name
            : $t("teaching.instructional.methodNone")
        }}
      </span>
    </div>

    <NewMatterPresentation
      v-if="oldMatter"
      :key="`${oldMatter.id}-${matter ? matter.id : 'base'}-${
        oldMatter.is_closed
      }-${matter ? matter.is_closed : ''}`"
      :Matter="matter"
      :oldMatter="oldMatter"
      :allows_crud="allows_crud"
    ></NewMatterPresentation>

    <div class="alert alert-warning py-2 small" v-if="load_error">
      {{ $t("teaching.instructional.loadError") }}
    </div>

    <div class="d-flex align-items-center justify-content-between mt-3 mb-2">
      <span v-if="allows_crud && canBuildDesign">
        <ButtonMedium
          :text_button="
            $t('reusable.common.createTooltip', { entity: containerEntityName })
          "
          :click_button="
            () => $bvModal.show(`instructional-module-create-modal`)
          "
          :icon="'plus'"
          :variant="'primary'"
          :custom_class="`mr-2`"
        ></ButtonMedium>
        <AiInstructionalBuilderModal
          v-if="showsAiQuickCreation"
          :egress_profile_matter_id="egress_profile_matter_id"
          :activities="activities"
          :level_key="levelKey"
          :anchor_id="anchorId"
          :available_hours="availableClassroomHours"
          :time_allocation_id="designTimeAllocationId"
          :indicators="aiIndicators"
          :strategies="methodological_strategies"
          :evidences="procedure_evidences"
          :study_units="studyUnits"
          :method_moments="moments"
          :instructional_design_id="
            instructionalDesign ? instructionalDesign.id : null
          "
          :design_summary="designSummary"
          :modules="modules"
          :show_button="true"
          @created="onAiCreated"
        ></AiInstructionalBuilderModal>
      </span>

      <span class="ml-auto">
        <ButtonMedium
          :text_button="$t('teaching.instructionalMatrix.openButton')"
          :tooltip_text="$t('teaching.instructionalMatrix.openTooltip')"
          :click_button="goToMatrix"
          :icon="'files'"
          :variant="'secondary'"
        ></ButtonMedium>
      </span>
    </div>

    <div v-if="moments.length === 0" class="alert alert-warning py-2 small">
      {{ $t("teaching.instructional.momentsMissing") }}
    </div>

    <template v-if="instructionalDesign">

      <DescriptorsContainer
        :ref_component="'InstructionalDesignTop'"
        :ProgramView="true"
        :warn_shared_title="false"
        :Title_text="$t('teaching.instructional.descriptorsTopTitle')"
        :Text_info_tooltip="$t('teaching.instructional.descriptorsTopHint')"
        :Title="topDescriptorTitles"
        :Descriptors="topDescriptors"
        :allows_crud="allows_crud && canBuildDesign"
        :Order="1"
        :VisibleOrder="false"
        :PagePosition="TOP_DESCRIPTOR_PAGE_POSITION"
        :can_update="allows_crud"
        :can_delete="allows_crud"
        :can_delete_title="false"
        :show_is_default="false"
        @updateOrder="updateOrderDesignDescriptors"
        @updateState="updateVisibleDesignDescriptor"
        @createDescription="createDesignDescriptor"
        @updateDescription="updateDesignDescriptor"
        @createTitle="createDescriptorTitle"
        @updateTitle="updateDescriptorTitle"
        @deleteDescriptors="deleteDesignDescriptor"
      ></DescriptorsContainer>


      <ModuleContainer
        v-for="module in modules"
        :key="module.id"
        :module="module"
        :instructional_design_id="instructionalDesign.id"
        :activities="activities"
        :moments="moments"
        :study_units="studyUnits"
        :bibliographic_resources="bibliographic_resources"
        :teaching_support_resources="supportResources"
        :activity_weeks="activityWeeks"
        :time_allocation_rows="time_allocation_rows"
        :evaluation_situations="evaluation_situations"
        :evaluation_list="activities"
        :highlight_activity_id="highlight_activity_id"
        :level_key="levelKey"
        :matter_base_id="baseMatterId"
        :egress_profile_matter_id="matter ? matter.id : null"
        :allows_crud="allows_crud"
        :shows_ai="showsAiQuickCreation"
        @reordered="renumberDesign"
        @created="createdDesignEvaluation"
        @resources-changed="refreshResourceCatalogs"
      ></ModuleContainer>

      <div v-if="allows_crud || looseActivities.length > 0" class="kl-loose">
        <template v-if="allows_crud">
          <h6 class="kl-loose-title">
            {{
              $t("teaching.instructional.activitiesWithoutModule", {
                activity: piecesEntityNamePlural,
                container: containerEntityName,
              })
            }}
          </h6>
        </template>
        <ActivityList
          :activities="looseActivities"
          :instructional_design_id="instructionalDesign.id"
          :module_id="null"
          :study_units="studyUnits"
          :bibliographic_resources="bibliographic_resources"
          :teaching_support_resources="supportResources"
          :activity_weeks="activityWeeks"
          :time_allocation_rows="time_allocation_rows"
          :evaluation_situations="evaluation_situations"
          :evaluation_list="activities"
          :highlight_activity_id="highlight_activity_id"
          :level_key="levelKey"
          :allows_crud="allows_crud"
          :shows_ai="showsAiQuickCreation"
          :egress_profile_matter_id="egress_profile_matter_id"
          @reordered="renumberDesign"
          @resources-changed="refreshResourceCatalogs"
        ></ActivityList>
      </div>

      <div class="mb-3">
        <ButtonMedium
          v-if="allows_crud && canBuildDesign"
          class="mr-1"
          :text_button="
            $t('reusable.common.createTooltip', { entity: activityEntityName })
          "
          :click_button="
            () => $bvModal.show(`instructional-activity-loose-modal`)
          "
          :icon="'plus'"
          :variant="'primary'"
        ></ButtonMedium>
      </div>

      <section v-if="designSummary || allows_crud" class="kl-design-summary">
        <div class="d-flex align-items-center">
          <h6 class="kl-summary-title flex-grow-1 mb-0">
            {{ $t("teaching.instructional.designSummary") }}
          </h6>

          <b-button
            v-if="allows_crud && showsAiQuickCreation"
            variant="link"
            size="sm"
            class="p-0"
            :disabled="redoing_summary"
            :title="$t('teaching.instructional.designSummaryRedo')"
            @click="redoDesignSummary()"
          >
            <b-spinner
              v-if="redoing_summary"
              small
              variant="primary"
            ></b-spinner>
            <b-icon v-else icon="arrow-repeat"></b-icon>
          </b-button>
        </div>
        <div class="mb-2"></div>
        <div
          v-if="allows_crud"
          class="kl-summary-box"
          @click="$bvModal.show('instructional-design-summary-modal')"
        >
          <RenderVHtml
            v-if="designSummary"
            v-b-tooltip.v-secondary.noninteractive.hover="{
              title: $t('teaching.instructional.designSummaryEdit'),
              boundary: 'viewport',
            }"
            :content="designSummary"
          ></RenderVHtml>
          <span v-else class="kl-summary-empty">
            {{ $t("teaching.instructional.designSummaryEmpty") }}
          </span>
        </div>
        <RenderVHtml v-else :content="designSummary"></RenderVHtml>
        <ContainerRichTextKlv3
          v-if="allows_crud"
          :id_modal="`instructional-design-summary-modal`"
          :title_modal="$t('teaching.instructional.designSummary')"
          :size_modal="`lg`"
          :content_rich_text="designSummary"
          :permit_blank="true"
          @save="saveDesignSummary"
        ></ContainerRichTextKlv3>
      </section>

      <DescriptorsContainer
        v-if="instructionalDesign"
        :ref_component="'InstructionalDesign'"
        :ProgramView="true"
        :warn_shared_title="false"
        :Title_text="$t('teaching.instructional.descriptorsTitle')"
        :Text_info_tooltip="$t('teaching.instructional.descriptorsHint')"
        :Title="bottomDescriptorTitles"
        :Descriptors="bottomDescriptors"
        :allows_crud="allows_crud && canBuildDesign"
        :Order="1"
        :VisibleOrder="false"
        :PagePosition="DESIGN_DESCRIPTOR_PAGE_POSITION"
        :can_update="allows_crud"
        :can_delete="allows_crud"
        :can_delete_title="false"
        :show_is_default="false"
        @updateOrder="updateOrderDesignDescriptors"
        @updateState="updateVisibleDesignDescriptor"
        @createDescription="createDesignDescriptor"
        @updateDescription="updateDesignDescriptor"
        @createTitle="createDescriptorTitle"
        @updateTitle="updateDescriptorTitle"
        @deleteDescriptors="deleteDesignDescriptor"
      ></DescriptorsContainer>

      <b-modal
        key="instructional-module-create-modal"
        :id="`instructional-module-create-modal`"
        :title="
          $t('reusable.common.createModalTitle', {
            entity: containerEntityName,
          })
        "
        size="lg"
        hide-footer
        ignore-enforce-focus-selector="#sidebar-right"
        @show="create_form_key++"
      >
        <ModuleForm
          :key="create_form_key"
          :module="null"
          :instructional_design_id="instructionalDesign.id"
          :next_order="modules.length + 1"
          @saved="closeCreateModal"
        ></ModuleForm>
      </b-modal>

      <b-modal
        key="instructional-activity-loose-modal"
        :id="`instructional-activity-loose-modal`"
        :title="
          $t('reusable.common.createModalTitle', { entity: activityEntityName })
        "
        size="xl"
        hide-footer
        ignore-enforce-focus-selector="#sidebar-right"
        @show="loose_form_key++"
      >

        <MatterProgramEvaluationForm
          v-if="levelKey === 'matter'"
          :key="loose_form_key"
          :Evaluation="null"
          :matter_id="baseMatterId"
          :EvaluationSituations="evaluation_situations"
          :MatEvaStuEnvTimAll="time_allocation_rows"
          :evaluation_list="activities"
          @created="
            (evaluation, time_rows, tests) =>
              createdDesignEvaluation(evaluation, time_rows, tests, null)
          "
          @created_support_resource="refreshResourceCatalogs"
        ></MatterProgramEvaluationForm>
        <EPMatterProgramEvaluationForm
          v-else
          :key="loose_form_key"
          :Evaluation="null"
          :egress_profile_matter_id="matter.id"
          :EvaluationSituations="evaluation_situations"
          :EPMatEvaStuEnvTimAll="time_allocation_rows"
          :evaluation_list="activities"
          @created="
            (evaluation, time_rows, tests) =>
              createdDesignEvaluation(evaluation, time_rows, tests, null)
          "
          @created_support_resource="refreshResourceCatalogs"
        ></EPMatterProgramEvaluationForm>
      </b-modal>
    </template>

    <InstructionalCoverageTable
      v-if="allows_crud"
      :activities="activities"
      :moments="moments"
      :study_units="studyUnits"
      :design_stats="designStats"
      :egress_profile_id="matter ? matter.egress_profile : null"
      :egress_profile_matter_id="matter ? matter.id : null"
      :matter_base_id="baseMatterId"
      :matter_name="coverageMatterName"
    ></InstructionalCoverageTable>

    <InstructionalSummaryTable
      :modules="modules"
      :activities="activities"
      :moments="moments"
      :activity_weeks="activityWeeks"
    ></InstructionalSummaryTable>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";
import useAiBuilderStream from "@/components/reusable/ai-builder/useAiBuilderStream";
import { createDesignEvaluation } from "./createDesignEvaluation";

export default {
  name: "Instructional",
  components: {
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
    NewMatterPresentation: () =>
      import(
        "@/components/teaching/MatterProgram/Components/NewMatterPresentation.vue"
      ),
    ContainerRichTextKlv3: () =>
      import("@/components/reusable/ContainerRichTextKlv3"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    InstructionalSummaryTable: () =>
      import("./components/InstructionalSummaryTable"),
    InstructionalCoverageTable: () =>
      import("./components/InstructionalCoverageTable"),
    ModuleContainer: () => import("./components/ModuleContainer"),
    DescriptorsContainer: () =>
      import("@/components/reusable/DescriptorsContainer"),
    ModuleForm: () => import("./components/ModuleForm"),
    ActivityList: () => import("./components/ActivityList"),
    AiInstructionalBuilderModal: () =>
      import("@/components/reusable/AiInstructionalBuilderModal"),
    MatterProgramEvaluationForm: () =>
      import(
        "@/components/teaching/MatterProgram/MatterProgramEvaluation/MatterProgramEvaluationForm"
      ),
    EPMatterProgramEvaluationForm: () =>
      import(
        "@/components/teaching/MatterProgram/EPMatterProgramEvaluation/EPMatterProgramEvaluationForm"
      ),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  props: {
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    matter_base_id: {
      type: Number,
      default: null,
    },
    highlight_activity_id: {
      type: Number,
      default: null,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    can_delete: {
      type: Boolean,
      default: false,
    },
    can_update: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      redoing_summary: false,
      load_error: false,
      bibliographic_resources: [],
      teaching_support_resources: [],
      teaching_support_resource_types: [],
      selected_method_id: null,
      saving_method: false,
      create_form_key: 0,
      loose_form_key: 0,
      time_allocation_rows: [],
      evaluation_situations: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
      matters: names.MATTERS,
      egress_profiles_matters: names.EGRESS_PROFILES_MATTERS,
      study_units: names.STUDY_UNITS,
      instructional_methods: names.INSTRUCTIONAL_METHODS,
      instructional_designs: names.INSTRUCTIONAL_DESIGNS,
      instructional_modules: names.INSTRUCTIONAL_MODULES,
      matter_evaluations: names.MATTER_EVALUATIONS,
      epm_evaluations: names.EGRESS_PROFILE_MATTER_EVALUATIONS,
      time_allocations: names.TIME_ALLOCATIONS,
      matter_time_allocations: names.MATTER_TIME_ALLOCATIONS,
      ep_matter_time_allocations: names.EP_MATTER_TIME_ALLOCATIONS,
      methodological_strategies: names.METHODOLOGICAL_STRATEGIES,
      procedure_evidences: names.PROCEDURE_EVIDENCES,
      evaluation_criterias_micro: names.EVALUATION_CRITERIAS,
      descriptor_titles: names.EGRESS_PROFILE_MATTER_DESCRIPTORS,
      design_descriptors: names.INSTRUCTIONAL_DESIGN_DESCRIPTORS,
    }),
    showsAiQuickCreation() {
      if (!this.allows_crud || !this.user || !this.institution) return false;
      if (!this.anchorId) return false;
      if (!this.institution.kl_ai_quick_creation) return false;
      if (this.user.user_data && !this.user.user_data.ai_component)
        return false;
      if (this.oldMatter && this.oldMatter.is_closed) return false;
      if (this.user.is_superuser) return true;
      return [
        "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
        "DIRECTIVOS ACADÉMICOS (CAMPUS)",
        "DIRECTIVOS FACULTAD O ESCUELA",
        "DIRECTORES DE CARRERA",
      ].some((group) => (this.user.group_names || []).includes(group));
    },
    availableClassroomHours() {
      const used = this.activities.reduce(
        (sum, activity) => sum + (this.activityHours(activity) || 0),
        0
      );
      return Math.max(
        Math.round((this.totalClassroomHours - used) * 10) / 10,
        0
      );
    },
    matterTimeAllocationRows() {
      if (!this.anchorId) return [];
      return this.levelKey === "matter"
        ? this.matter_time_allocations.filter((x) => x.matter == this.anchorId)
        : this.ep_matter_time_allocations.filter(
            (x) => x.egress_profile_matter == this.anchorId
          );
    },
    countedTimeAllocationIds() {
      return this.matterTimeAllocationRows
        .filter((x) => x.counts_towards_credits == true)
        .map((x) => x.time_allocation);
    },
    designTimeAllocationId() {
      const row = this.matterTimeAllocationRows.find(
        (x) => x.counts_towards_credits == true && Number(x.hours) > 0
      );
      return row ? row.time_allocation : null;
    },
    aiIndicators() {
      if (!this.baseMatterId) return [];
      const unit_by_id = new Map(
        this.studyUnits.map((unit) => [unit.id, unit])
      );
      return this.evaluation_criterias_micro
        .filter((x) => unit_by_id.has(x.study_unit))
        .map((indicator) => ({
          id: indicator.id,
          study_unit: indicator.study_unit,
          code: `${unit_by_id.get(indicator.study_unit).order}.${
            indicator.order
          }`,
          name: indicator.full_sentence || indicator.sentence || "",
        }));
    },
    DESIGN_DESCRIPTOR_PAGE_POSITION() {
      return 3;
    },
    TOP_DESCRIPTOR_PAGE_POSITION() {
      return 5;
    },
    topDescriptorTitles() {
      return this.usedDescriptorTitles(this.TOP_DESCRIPTOR_PAGE_POSITION);
    },
    bottomDescriptorTitles() {
      return this.usedDescriptorTitles(this.DESIGN_DESCRIPTOR_PAGE_POSITION);
    },
    topDescriptors() {
      return this.designDescriptors.filter(
        (x) => this.descriptorPosition(x) == this.TOP_DESCRIPTOR_PAGE_POSITION
      );
    },
    bottomDescriptors() {
      return this.designDescriptors.filter(
        (x) => this.descriptorPosition(x) != this.TOP_DESCRIPTOR_PAGE_POSITION
      );
    },
    designDescriptors() {
      if (!this.instructionalDesign) return [];
      return this.design_descriptors.filter(
        (x) => x.instructional_design == this.instructionalDesign.id
      );
    },
    designSummary() {
      return (this.instructionalDesign || {}).summary || "";
    },
    designWeeks() {
      if (!this.levelKey) return 0;
      if (this.levelKey === "matter")
        return this.oldMatter ? this.oldMatter.weeks : 0;
      return this.matter ? this.matter.weeks : 0;
    },
    totalClassroomHours() {
      const counted = this.countedTimeAllocationIds;
      if (counted.length === 0) return 0;
      return this.matterTimeAllocationRows
        .filter((x) => counted.includes(x.time_allocation))
        .reduce((sum, x) => sum + Number(x.hours || 0), 0);
    },
    weeklyClassroomHours() {
      if (!this.designWeeks) return 0;
      const total = this.totalClassroomHours;
      if (total <= 0) return 0;
      return total / this.designWeeks;
    },
    activityWeeks() {
      const weeks_map = new Map();
      const weekly = this.weeklyClassroomHours;
      const total_weeks = this.designWeeks;
      if (weekly <= 0) {
        this.activities.forEach((activity) =>
          weeks_map.set(activity.id, { reason: "no_classroom_hours" })
        );
        return weeks_map;
      }
      let accumulated = 0;
      let broken = false;
      this.designSequence.forEach((activity) => {
        if (broken) {
          weeks_map.set(activity.id, { reason: "chain_broken" });
          return;
        }
        const hours = this.activityHours(activity);
        if (hours === null) {
          broken = true;
          weeks_map.set(activity.id, { reason: "no_hours" });
          return;
        }
        const from = Math.floor(accumulated / weekly) + 1;
        accumulated += hours;
        const to = Math.max(from, Math.ceil(accumulated / weekly));
        weeks_map.set(activity.id, {
          from: from,
          to: to,
          overflow: total_weeks > 0 && to > total_weeks,
        });
      });
      return weeks_map;
    },
    designSequence() {
      const sequence = [];
      [...this.modules]
        .sort((a, b) => a.order - b.order)
        .forEach((module) => {
          sequence.push(...this.orderedModuleActivities(module));
        });
      sequence.push(
        ...[...this.looseActivities].sort(
          (a, b) => a.order - b.order || a.id - b.id
        )
      );
      return sequence;
    },
    designStats() {
      const used_hours = this.activities.reduce(
        (sum, activity) => sum + (this.activityHours(activity) || 0),
        0
      );
      let occupied_weeks = 0;
      this.activityWeeks.forEach((week) => {
        if (week && week.to) occupied_weeks = Math.max(occupied_weeks, week.to);
      });
      return {
        containers: this.modules.length,
        unplaced: this.looseActivities.length,
        used_hours: used_hours,
        total_hours: this.totalClassroomHours,
        occupied_weeks: occupied_weeks,
        total_weeks: this.designWeeks,
        weighing: this.activities.reduce(
          (sum, activity) => sum + Number(activity.weighing || 0),
          0
        ),
      };
    },
    containerEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        false,
        "Módulo"
      );
    },
    containerEntityNamePlural() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        true,
        "Módulos"
      );
    },
    activityEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        false,
        "Actividad"
      );
    },
    activityEntityNamePlural() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        true,
        "Actividades"
      );
    },
    piecesEntityNamePlural() {
      return `${this.activityEntityNamePlural}/${this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      )}`;
    },
    matter() {
      if (!this.egress_profile_matter_id) return null;
      return this.egress_profiles_matters.find(
        (x) => x.id == this.egress_profile_matter_id
      );
    },
    isBaseLevel() {
      return !!this.matter_base_id;
    },
    baseMatterId() {
      if (this.isBaseLevel) return this.matter_base_id;
      return this.matter ? this.matter.matter : null;
    },
    oldMatter() {
      if (!this.baseMatterId) return null;
      return this.matters.find((x) => x.id == this.baseMatterId);
    },
    coverageMatterName() {
      if (this.matter && this.matter.matter__name) return this.matter.matter__name;
      return this.oldMatter ? this.oldMatter.name : "";
    },
    aiLevelPayload() {
      return this.egress_profile_matter_id
        ? { egress_profile_matter: this.egress_profile_matter_id }
        : { matter: this.matter_base_id };
    },
    anchorId() {
      return this.levelKey === "matter"
        ? this.baseMatterId
        : this.matter
        ? this.matter.id
        : null;
    },
    methodOptions() {
      const options = this.instructional_methods.map((method) => ({
        value: method.id,
        text: method.name,
      }));
      options.unshift({
        value: null,
        text:
          options.length > 0
            ? this.$t("teaching.instructional.methodPlaceholder")
            : this.$t("teaching.instructional.methodsEmpty"),
      });
      return options;
    },
    canBuildDesign() {
      return (
        this.instructionalDesign != null &&
        this.activeMethod != null &&
        this.moments.length > 0
      );
    },
    activeMethod() {
      if (!this.instructionalDesign || !this.instructionalDesign.method)
        return null;
      return (
        this.instructional_methods.find(
          (x) => x.id == this.instructionalDesign.method
        ) || null
      );
    },
    levelKey() {
      if (this.isBaseLevel) return "matter";
      if (!this.matter) return null;
      return this.matter.use_matter_values ? "matter" : "egress_profile_matter";
    },
    supportResources() {
      const type_by_id = new Map(
        this.teaching_support_resource_types.map((type) => [type.id, type])
      );
      return [...this.teaching_support_resources]
        .map((resource) => {
          const type = type_by_id.get(resource.type) || null;
          return {
            id: resource.id,
            name: resource.name,
            description: resource.description,
            type: resource.type,
            type_name: type ? type.name : "",
            type_order: type ? type.order : 0,
          };
        })
        .sort(
          (a, b) =>
            a.type_order - b.type_order ||
            a.type - b.type ||
            (a.description || a.name || "").localeCompare(
              b.description || b.name || ""
            )
        );
    },
    instructionalDesign() {
      if (!this.anchorId) return null;
      if (this.levelKey === "matter") {
        return (
          this.instructional_designs.find((x) => x.matter == this.anchorId) ||
          null
        );
      }
      if (this.levelKey === "egress_profile_matter") {
        return (
          this.instructional_designs.find(
            (x) => x.egress_profile_matter == this.anchorId
          ) || null
        );
      }
      return null;
    },
    modules() {
      if (!this.instructionalDesign) return [];
      return this.instructional_modules.filter(
        (x) => x.instructional_design == this.instructionalDesign.id
      );
    },
    activities() {
      if (!this.anchorId) return [];
      if (this.levelKey === "matter") {
        return this.matter_evaluations.filter((x) => x.matter == this.anchorId);
      }
      return this.epm_evaluations.filter(
        (x) => x.egress_profile_matter == this.anchorId
      );
    },
    studyUnits() {
      if (!this.baseMatterId) return [];
      const unidades = this.study_units
        .filter((x) => x.matter == this.baseMatterId)
        .sort((a, b) => a.order - b.order || a.id - b.id);
      const distingue =
        new Set(unidades.map((x) => x.order)).size === unidades.length;
      return unidades.map((unit, index) => ({
        ...unit,
        name: this.studyUnitLabel(unit, distingue ? unit.order : index + 1),
      }));
    },
    looseActivities() {
      return this.activities.filter((x) => x.module == null);
    },
    moments() {
      if (!this.activeMethod) return [];
      return [...(this.activeMethod.moments || [])].sort(
        (a, b) => a.order - b.order
      );
    },
    breadcrumb_items() {
      let breadcrumb = [];
      if (this.isBaseLevel) {
        return [
          {
            text: this.$t("reusable.breadcrumb.home"),
            to: "/home",
          },
          {
            text: this.$getVisibleNames(
              "mesh.egressprofilematter",
              true,
              "Asignaturas"
            ),
            to: "/matter-list",
          },
          {
            text: this.$getVisibleNames(
              "manual.matter_program",
              false,
              "Programa de la Asignatura"
            ),
            to: `/old-matter/form/${this.matter_base_id}`,
          },
          {
            text: this.$t("teaching.instructional.title"),
            active: true,
          },
        ];
      }
      if (this.matter && this.matter.egress_profile) {
        breadcrumb = [
          {
            text: this.$t("reusable.breadcrumb.home"),
            to: "/home",
          },
          {
            text: this.$getVisibleNames("mesh.career", true, "Programas"),
            to: "/careers",
          },
          {
            text: this.$getVisibleNames("manual.matriz_2", false, "Matriz 2"),
            to: `/matrix2/form/${this.matter.egress_profile}`,
            dropdownOptions: [
              "showEgressProfile",
              "showMatrix1",
              "showMatrix2",
              "showMatrix3",
              "showMatrix4",
              "showStudyPlan",
              "showCurricularMesh",
            ],
          },
          {
            text: this.$getVisibleNames(
              "manual.matter_program",
              false,
              "Programa de la Asignatura"
            ),
            to: `/matter/form/${this.egress_profile_matter_id}`,
            dropdownOptions: ["showMatterProgram", "showPlannification"],
          },
        ];
      }
      return breadcrumb;
    },
  },
  methods: {
    goToMatrix() {
      this.$router.push(
        this.isBaseLevel
          ? {
              name: "InstructionalMatrixMatterView",
              params: { matter_id: this.matter_base_id },
            }
          : {
              name: "InstructionalMatrixView",
              params: { matter_id: this.egress_profile_matter_id },
            }
      );
    },
    studyUnitLabel(unit, order) {
      const nombre = (unit.name || "").trim();
      if (nombre) return nombre;
      const frase = (unit.full_sentence || unit.sentence || "").trim();
      const numero = this.$t("teaching.instructional.studyUnitFallback", {
        order: order != null ? order : unit.order,
      });
      if (!frase) return numero;
      return `${numero}: ${frase}`;
    },
    orderedModuleActivities(module) {
      return this.activities
        .filter((x) => x.module == module.id)
        .sort((a, b) => a.order - b.order || a.id - b.id);
    },
    renumberDesign() {
      this.designSequence.forEach((activity, index) => {
        const order = index + 1;
        if (
          activity._patched_order === order &&
          activity._patched_module === activity.module
        )
          return;
        activity._patched_order = order;
        activity._patched_module = activity.module;
        activity.order = order;
        this.patchActivityPlacement(activity);
      });
    },
    patchActivityPlacement(activity) {
      const item = { order: activity.order, module: activity.module };
      const action =
        this.levelKey === "matter"
          ? [
              names.PATCH_MATTER_EVALUATION,
              { matter_evaluation_id: activity.id, item: item },
            ]
          : [
              names.PATCH_EGRESS_PROFILE_MATTER_EVALUATION,
              { egress_profile_matter_evaluation_id: activity.id, item: item },
            ];
      this.$store.dispatch(action[0], action[1]).catch(() => {
        toast(this.$t("teaching.instructional.saveError"));
      });
    },
    handleLoadError() {
      this.load_error = true;
    },
    async redoDesignSummary() {
      if (!this.instructionalDesign || !this.anchorId) return;
      if (this.designSummary) {
        const sigue = await this.$bvModal.msgBoxConfirm(
          this.$t("teaching.instructional.designSummaryRedoConfirm"),
          {
            title: this.$t("teaching.instructional.designSummaryRedo"),
            okTitle: this.$t("teaching.instructional.designSummaryRedo"),
            cancelTitle: this.$t("reusable.common.cancel"),
            okVariant: "primary",
            centered: true,
          }
        );
        if (!sigue) return;
      }
      this.redoing_summary = true;
      try {
        const { callBuilder } = useAiBuilderStream(
          {
            url: `/common/instructional-builder-ai/`,
            generate_error_key:
              "reusable.aiInstructionalBuilderModal.generateError",
          },
          this
        );
        const result = await callBuilder({
          ...this.aiLevelPayload,
          summary_only: true,
        });
        const texto = (result || {}).design_summary || "";
        if (!texto) {
          toast(this.$t("teaching.instructional.saveError"));
          return;
        }
        await this.$store.dispatch(names.PATCH_INSTRUCTIONAL_DESIGN, {
          instructional_design_id: this.instructionalDesign.id,
          item: { summary: texto },
        });
        toast(this.$t("teaching.instructional.saved"));
      } catch (error) {
        toast(error.message || this.$t("teaching.instructional.saveError"));
      } finally {
        this.redoing_summary = false;
      }
    },
    saveDesignSummary(content) {
      if (!this.instructionalDesign) return;
      this.$store
        .dispatch(names.PATCH_INSTRUCTIONAL_DESIGN, {
          instructional_design_id: this.instructionalDesign.id,
          item: { summary: content },
        })
        .then(() => toast(this.$t("teaching.instructional.saved")))
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    changeMethod(method_id) {
      if (!this.instructionalDesign) return;
      if (this.instructionalDesign.method == method_id) return;
      const previous = this.instructionalDesign.method || null;
      this.saving_method = true;
      this.$store
        .dispatch(names.PATCH_INSTRUCTIONAL_DESIGN, {
          instructional_design_id: this.instructionalDesign.id,
          item: { method: method_id },
        })
        .then(() => {
          this.saving_method = false;
          toast(this.$t("teaching.instructional.methodUpdated"));
        })
        .catch(() => {
          this.saving_method = false;
          this.selected_method_id = previous;
          toast(this.$t("teaching.instructional.saveError"));
        });
    },
    onAiCreated() {
      this.fetchEvaluations();
      this.fetchEvaluationFormCatalogs();
    },
    closeCreateModal() {
      this.$bvModal.hide(`instructional-module-create-modal`);
    },
    closeLooseModal() {
      this.$bvModal.hide(`instructional-activity-loose-modal`);
    },
    createdDesignEvaluation(evaluation, time_rows, tests, module_id) {
      if (!this.anchorId || !this.levelKey) return;
      createDesignEvaluation(this, {
        level_key: this.levelKey,
        anchor_id: this.anchorId,
        evaluation: evaluation,
        time_rows: time_rows,
        tests: tests,
        placement: {
          module: module_id,
          order: this.activities.length + 1,
        },
      })
        .then(() => {
          this.fetchEvaluations();
          this.fetchEvaluationFormCatalogs();
          toast(this.$t("teaching.instructional.saved"));
          this.closeLooseModal();
          this.$bvModal.hide(
            `instructional-module-${module_id}-new-activity-modal`
          );
        })
        .catch(() => {
          toast(this.$t("teaching.instructional.saveError"));
        });
    },
    fetchEvaluationFormCatalogs() {
      if (!this.anchorId || !this.levelKey) return;
      const url =
        this.levelKey === "matter"
          ? `/mesh/mat-eva-stu-env-tim-all/?matter=${this.anchorId}`
          : `/mesh/egr-pro-mat-eva-stu-env-tim-all/?egress_profile_matter=${this.anchorId}`;
      this.$restful.GetAll(url).then((response) => {
        this.time_allocation_rows = response;
      });
      if (this.evaluation_situations.length === 0)
        this.$restful
          .GetAll(`/teaching/evaluation-situation/`)
          .then((response) => {
            this.evaluation_situations = response;
          });
    },
    fetchCatalogs() {
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_METHODS, true)
        .catch(this.handleLoadError);
      this.$store.dispatch(names.FETCH_TIME_ALLOCATIONS);
      this.$store.dispatch(names.FETCH_METHODOLOGICAL_STRATEGIES);
      this.$store.dispatch(names.FETCH_PROCEDURE_EVIDENCES);
      this.$store.dispatch(names.FETCH_INSTRUCTIONAL_STATUSES);
      this.$store.dispatch(names.FETCH_EGRESS_PROFILE_MATTER_DESCRIPTORS, {});
      this.$store.dispatch(names.FETCH_STUDY_ENVIRONMENTS);
      this.$store.dispatch(names.FETCH_STUDY_ENVIRONMENTS_TIME_ALLOCATION);
      this.$store.dispatch(names.FETCH_REGIMES);
      this.$store.dispatch(names.FETCH_EPM_EVALUATION_TYPES);
      this.$store.dispatch(names.FETCH_AUTONOMOUS_TEACHING_ACTIVITIES);
      this.$store.dispatch(names.FETCH_NEW_TEST_TYPES);
    },
    refreshResourceCatalogs() {
      if (!this.baseMatterId) return;
      this.fetchBibliographicResources(this.baseMatterId);
      this.fetchTeachingSupportResources(this.baseMatterId);
    },
    fetchBibliographicResources(matter_id) {
      this.$restful
        .GetAll(`/teaching/bibliographic-resource/?matter=${matter_id}`)
        .then((response) => {
          this.bibliographic_resources = response;
        })
        .catch(this.handleLoadError);
    },
    fetchTeachingSupportResources(matter_id) {
      this.$restful
        .GetAll(`/teaching/support-resource/?matter=${matter_id}`)
        .then((response) => {
          this.teaching_support_resources = response;
        })
        .catch(this.handleLoadError);
      this.$restful
        .GetAll(`/teaching/support-resource-type/`)
        .then((response) => {
          this.teaching_support_resource_types = response;
        })
        .catch(this.handleLoadError);
    },
    activityHours(activity) {
      const counted = this.countedTimeAllocationIds;
      if (counted.length === 0) return null;
      const key =
        this.levelKey === "matter"
          ? "matter_evaluation"
          : "egress_profile_matter_evaluation";
      const hours = this.time_allocation_rows
        .filter(
          (x) => x[key] == activity.id && counted.includes(x.time_allocation)
        )
        .reduce((sum, x) => sum + Number(x.hours || 0), 0);
      if (!hours || isNaN(hours) || hours <= 0) return null;
      return hours;
    },
    fetchDesign() {
      if (!this.anchorId || !this.levelKey) return;
      const payload =
        this.levelKey === "matter"
          ? { matter_id: this.anchorId }
          : { egress_profile_matter_id: this.anchorId };
      this.$store
        .dispatch(names.RESOLVE_INSTRUCTIONAL_DESIGN, payload)
        .then((design) => {
          if (design) this.fetchDesignChildren(design.id);
        })
        .catch(this.handleLoadError);
    },
    descriptorPosition(descriptor) {
      const title = this.descriptor_titles.find(
        (x) => x.id == descriptor.descriptor
      );
      return title ? title.page_position : null;
    },
    usedDescriptorTitles(page_position) {
      const usados = new Set(this.designDescriptors.map((x) => x.descriptor));
      return this.descriptor_titles.filter(
        (x) => x.page_position == page_position && usados.has(x.id)
      );
    },
    createDescriptorTitle(title) {
      if (!isNaN(title.id)) return;
      this.$store
        .dispatch(names.POST_EGRESS_PROFILE_MATTER_DESCRIPTORS, {
          ...title,
          is_default: false,
        })
        .then((response) => this.createDesignDescriptor("", response))
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    updateDescriptorTitle(title) {
      if (isNaN(title.id)) return;
      this.$store.dispatch(names.PATCH_EGRESS_PROFILE_MATTER_DESCRIPTORS, {
        id: title.id,
        item: { title: title.title, is_default: false },
      });
    },
    createDesignDescriptor(text, title) {
      if (isNaN(title.id) || !this.instructionalDesign) return;
      const en_la_zona =
        title.page_position == this.TOP_DESCRIPTOR_PAGE_POSITION
          ? this.topDescriptors
          : this.bottomDescriptors;
      this.$store
        .dispatch(names.POST_INSTRUCTIONAL_DESIGN_DESCRIPTOR, {
          instructional_design: this.instructionalDesign.id,
          descriptor: title.id,
          description: text,
          order: en_la_zona.length + 1,
        })
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    updateDesignDescriptor(descriptor, text) {
      if (isNaN(descriptor.id)) return;
      this.$store
        .dispatch(names.PATCH_INSTRUCTIONAL_DESIGN_DESCRIPTOR, {
          instructional_design_descriptor_id: descriptor.id,
          item: { description: text },
        })
        .then(() => toast(this.$t("teaching.instructional.saved")))
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    updateVisibleDesignDescriptor(descriptor) {
      if (isNaN(descriptor.id)) return;
      this.$store.dispatch(names.PATCH_INSTRUCTIONAL_DESIGN_DESCRIPTOR, {
        instructional_design_descriptor_id: descriptor.id,
        item: { is_visible: !descriptor.is_visible },
      });
    },
    updateOrderDesignDescriptors(descriptors) {
      descriptors.forEach((descriptor) => {
        if (isNaN(descriptor.id)) return;
        if (
          this.design_descriptors.findIndex((x) => x.id == descriptor.id) == -1
        )
          return;
        this.$store.dispatch(names.PATCH_INSTRUCTIONAL_DESIGN_DESCRIPTOR, {
          instructional_design_descriptor_id: descriptor.id,
          item: { order: descriptor.order },
        });
      });
    },
    deleteDesignDescriptor(descriptor) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle2", {
          entity: this.$t("teaching.instructional.descriptor"),
          article: "el",
        }),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (!result.value) return;
        this.$store
          .dispatch(names.DELETE_INSTRUCTIONAL_DESIGN_DESCRIPTOR, descriptor.id)
          .then(() => this.deleteOrphanTitle(descriptor.descriptor))
          .catch(() => toast(this.$t("teaching.instructional.saveError")));
      });
    },
    deleteOrphanTitle(descriptor_title_id) {
      if (descriptor_title_id == null) return;
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_DESIGN_DESCRIPTORS, {
          descriptor_id: descriptor_title_id,
        })
        .then((rows) => {
          if ((rows || []).length > 0) return;
          this.$store.dispatch(
            names.DELETE_EGRESS_PROFILE_MATTER_DESCRIPTORS,
            descriptor_title_id
          );
        });
    },
    fetchDesignChildren(instructional_design_id) {
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_MODULES, {
          instructional_design_id: instructional_design_id,
        })
        .catch(this.handleLoadError);
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_UNITS, {
          instructional_design_id: instructional_design_id,
        })
        .catch(this.handleLoadError);
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_DESIGN_DESCRIPTORS, {
          instructional_design_id: instructional_design_id,
        })
        .catch(this.handleLoadError);
    },
    fetchEvaluations() {
      if (!this.anchorId || !this.levelKey) return;
      const action =
        this.levelKey === "matter"
          ? [names.FETCH_MATTER_EVALUATIONS, this.anchorId]
          : [names.FETCH_EGRESS_PROFILE_MATTER_EVALUATIONS, this.anchorId];
      this.$store.dispatch(action[0], action[1]).catch(this.handleLoadError);
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_ACTIVITY_DESCRIPTORS, {
          ...(this.levelKey === "matter"
            ? { matter: this.anchorId }
            : { egress_profile_matter: this.anchorId }),
        })
        .catch(this.handleLoadError);
    },
  },
  watch: {
    activeMethod: {
      immediate: true,
      handler(value) {
        this.selected_method_id = value ? value.id : null;
      },
    },
    levelKey: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.fetchDesign();
        this.fetchEvaluations();
      },
    },
    baseMatterId: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.$store.dispatch(names.FETCH_MATTER, value);
        this.$store
          .dispatch(names.FETCH_STUDY_UNITS, value)
          .then((study_units) => {
            study_units.forEach((study_unit) => {
              this.$store.dispatch(names.FETCH_CONTENTS, study_unit.id);
            });
          });
        this.fetchBibliographicResources(value);
        this.fetchTeachingSupportResources(value);
        this.$store.dispatch(names.FETCH_MATTER_TIME_ALLOCATIONS, {
          matter_id: value,
        });
        this.fetchEvaluationFormCatalogs();
        this.$store.dispatch(names.FETCH_EVALUATION_CRITERIAS, {
          matter_id: value,
        });
        this.$store.dispatch(names.FETCH_EVALUATION_CRITERIAS_MACROS, value);
        if (this.institution && this.institution.show_competence_unit)
          this.$store.dispatch(names.FETCH_COMPETENCE_UNITS, {
            matter__id: value,
          });
      },
    },
    matter: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.$store.dispatch(names.FETCH_EP_MATTER_TIME_ALLOCATIONS, {
          egress_profile_matter_id: value.id,
        });
      },
    },
  },
  mounted() {},
  created() {
    this.fetchCatalogs();
  },
};
</script>

<style scoped>
.kl-method-label {
  font-size: var(--kl-fs-small);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 700);
}
.kl-method-select {
  width: auto;
  min-width: 14rem;
  max-width: 22rem;
}
.kl-method {
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw, 600);
  background-color: #eaf1fb;
  color: #2f5aa0;
  border: 1px solid #d3e0f5;
  border-radius: var(--kl-radius-pill, 999px);
  padding: var(--kl-chip-padding, 2px 8px);
}
.kl-loose {
  border: 1px dashed #bcd3d0;
  border-radius: var(--kl-radius-md, 10px);
  padding: 0.75rem 0.9rem;
  margin-bottom: 1.1rem;
  background-color: #fbfdfc;
}
.kl-loose-title {
  font-size: var(--kl-fs-caption);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 700);
  margin-bottom: 0.6rem;
}
.kl-section-title {
  font-size: var(--kl-fs-caption);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 700);
}
.kl-design-summary {
  text-align: left;
  background-color: #fff;
  border: 1px solid var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 8px);
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
}
.kl-summary-title {
  font-size: var(--kl-fs-caption);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 700);
  margin-bottom: 0.45rem;
}
.kl-summary-box {
  cursor: pointer;
  border-radius: var(--kl-radius-md, 8px);
  padding: 0.25rem 0.4rem;
  margin: -0.25rem -0.4rem;
}
.kl-summary-box:hover {
  background-color: #f7fbfa;
}
.kl-summary-empty {
  color: #97a5a3;
  font-style: italic;
}
</style>
